
import * as THREE from "../libs/three.js/build/three.module.js";
import { EventDispatcher } from "./EventDispatcher.js";

/**
 * Represents a node in a point cloud tree structure. This class is an abstract base class
 * and should be overridden with specific implementations for different types of point cloud data.
 * 
 * @extends EventDispatcher
 */
export class PointCloudTreeNode extends EventDispatcher{
	/**
     * Creates an instance of PointCloudTreeNode.
     */
	constructor(){
		super();
		/**
         * Indicates whether the node's transformation needs to be updated.
         * 
         * @type {boolean}
         */
		this.needsTransformUpdate = true;
	}
	/**
     * Retrieves the children of this node. This function should be overridden by subclasses.
     * 
     * @abstract
     */
	getChildren () {
		throw new Error('override function');
	}
	/**
     * Retrieves the bounding box of this node. This function should be overridden by subclasses.
     * 
     * @abstract
     */
	getBoundingBox () {
		throw new Error('override function');
	}

	/**
     * Determines if this node's data is fully loaded. This function should be overridden by subclasses.
     * 
     * @abstract
     */
	isLoaded () {
		throw new Error('override function');
	}

	/**
     * Determines if this node is a geometry node. This function should be overridden by subclasses.
     * 
     * @abstract
     */
	isGeometryNode () {
		throw new Error('override function');
	}
	/**
     * Determines if this node is a tree node. This function should be overridden by subclasses.
     * 
     * @abstract
     */
	isTreeNode () {
		throw new Error('override function');
	}
	/**
     * Retrieves the level of this node in the point cloud tree. This function should be overridden by subclasses.
     * 
     * @abstract
     */
	getLevel () {
		throw new Error('override function');
	}
	/**
     * Retrieves the bounding sphere of this node. This function should be overridden by subclasses.
     * 
     * @abstract
     */
	getBoundingSphere () {
		throw new Error('override function');
	}
};
/**
 * Represents a point cloud tree structure. This class is a base class for different
 * types of point cloud tree implementations and is derived from THREE.Object3D.
 * 
 * @extends THREE.Object3D
 */
export class PointCloudTree extends THREE.Object3D {
	/**
     * Creates an instance of PointCloudTree.
     */
	constructor () {
		super();
		/**
         * The root node of the point cloud tree.
         * @type {PointCloudTreeNode}
         */
        this.root = null;
	}
	/**
     * Determines if the point cloud tree has been initialized.
     * 
     * @returns {boolean} True if the tree is initialized, otherwise false.
     */
	initialized () {
		return this.root !== null;
	}
};
